import {
  Heart,
  Lightbulb,
  DollarSign,
  Scale,
  Users,
  Target,
  TrendingUp,
  Wallet,
  CreditCard,
  Apple,
  Dumbbell,
  Brain,
  Shield,
  User,
  Compass,
  Award,
  PiggyBank,
} from "lucide-react";
import { useState, useEffect } from "react";

export default function FoundationSection() {
  const [animationStarted, setAnimationStarted] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setAnimationStarted(true), 500);
    return () => clearTimeout(timer);
  }, []);

  const lifeFramework = {
    Love: {
      color: "rgb(239, 68, 68)",
      icon: Heart,
      description: "Relationships and connections",
      subcategories: [
        { name: "Family", icon: Users, currentScore: 6.2, potentialScore: 8.1 },
        { name: "Friends", icon: User, currentScore: 5.8, potentialScore: 7.5 },
        {
          name: "Romance",
          icon: Heart,
          currentScore: 6.5,
          potentialScore: 8.2,
        },
      ],
    },
    Inspiration: {
      color: "rgb(59, 130, 246)",
      icon: Lightbulb,
      description: "Purpose and fulfillment",
      subcategories: [
        { name: "Flow", icon: Target, currentScore: 5.4, potentialScore: 7.8 },
        {
          name: "Direction",
          icon: Compass,
          currentScore: 5.1,
          potentialScore: 7.2,
        },
        {
          name: "Successes",
          icon: Award,
          currentScore: 6.0,
          potentialScore: 7.9,
        },
      ],
    },
    Finance: {
      color: "rgb(34, 197, 94)",
      icon: DollarSign,
      description: "Financial well-being",
      subcategories: [
        {
          name: "Income",
          icon: Wallet,
          currentScore: 5.8,
          potentialScore: 7.4,
        },
        {
          name: "Finances",
          icon: PiggyBank,
          currentScore: 4.9,
          potentialScore: 7.1,
        },
      ],
    },
    Equilibrium: {
      color: "rgb(168, 85, 247)",
      icon: Scale,
      description: "Health and balance",
      subcategories: [
        {
          name: "Nutrition",
          icon: Apple,
          currentScore: 6.1,
          potentialScore: 8.0,
        },
        {
          name: "Activity",
          icon: Dumbbell,
          currentScore: 6.8,
          potentialScore: 8.3,
        },
        {
          name: "Mental Health",
          icon: Brain,
          currentScore: 5.7,
          potentialScore: 7.6,
        },
        {
          name: "Safety",
          icon: Shield,
          currentScore: 7.2,
          potentialScore: 8.5,
        },
      ],
    },
  };

  // Get all subcategories for the 12-point radar chart
  const allSubcategories = Object.entries(lifeFramework).flatMap(
    ([category, data]) =>
      data.subcategories.map((sub) => ({
        ...sub,
        category,
        categoryColor: data.color,
      }))
  );

  return (
    <section className="py-24 md:py-32 bg-secondary/50 border-y-2 border-border">
      <div className="container mx-auto px-4 md:px-8 xl:px-12">
        <div className="max-w-7xl mx-auto space-y-24">
          {/* Section 1: The LIFE Framework Model */}
          <div className="text-center space-y-16">
            {/* Header */}
            <div className="space-y-6">
              <span className="text-primary text-sm font-bold tracking-wider uppercase">
                THE LIFE FRAMEWORK
              </span>
              <h2 className="text-4xl md:text-5xl xl:text-6xl font-bold leading-tight text-foreground">
                We start by scoring you across{" "}
                <span className="text-primary">12 key life areas</span>
              </h2>
              <p className="text-xl md:text-2xl text-muted-foreground leading-relaxed max-w-4xl mx-auto">
                This assessment becomes your personal baseline and roadmap for
                targeted improvement.
              </p>
            </div>

            {/* LIFE Framework Visual - Emphasis on subcategories */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {Object.entries(lifeFramework).map(([category, data], index) => {
                const IconComponent = data.icon;
                return (
                  <div
                    key={category}
                    className={`transition-all duration-700 delay-${
                      index * 200
                    } ${
                      animationStarted
                        ? "opacity-100 translate-y-0"
                        : "opacity-0 translate-y-8"
                    }`}
                  >
                    <div className="bg-card border-2 border-border p-6 h-full space-y-6 hover:border-primary/30 transition-colors">
                      {/* Category Header - Just text, no circle */}
                      <div className="space-y-2">
                        <h3 className="text-lg font-bold text-foreground">
                          {category}
                        </h3>
                      </div>

                      {/* Subcategories - More prominent with icons */}
                      <div className="space-y-4">
                        {data.subcategories.map((sub, subIndex) => {
                          const SubIcon = sub.icon;
                          return (
                            <div
                              key={subIndex}
                              className="flex items-center gap-3 p-3 bg-muted/30 border border-border hover:border-primary/30 transition-colors"
                            >
                              <div
                                className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                                style={{ backgroundColor: data.color }}
                              >
                                <SubIcon className="w-4 h-4 text-white" />
                              </div>
                              <span className="text-foreground font-semibold text-base">
                                {sub.name}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Section 2: The Improvement Journey */}
          <div className="space-y-16">
            {/* Header */}
            <div className="text-center space-y-6">
              <span className="text-primary text-sm font-bold tracking-wider uppercase">
                YOUR IMPROVEMENT JOURNEY
              </span>
              <h2 className="text-4xl md:text-5xl xl:text-6xl font-bold leading-tight text-foreground">
                Then we focus on your{" "}
                <span className="text-primary">lowest scores first</span>
              </h2>
              <p className="text-xl md:text-2xl text-muted-foreground leading-relaxed max-w-4xl mx-auto">
                Personalized interventions that move the needle where it matters
                most.
              </p>
            </div>

            {/* Radar Chart Section - Full width */}
            <div className="flex justify-center">
              <RadarChart
                subcategories={allSubcategories}
                animated={animationStarted}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// Separate Radar Chart Component with proper positioning
function RadarChart({ subcategories, animated }) {
  const size = 400;
  const center = size / 2;
  const radius = 140;
  const labelRadius = 180;

  // Calculate positions for each point
  const points = subcategories.map((sub, index) => {
    const angle = (index / subcategories.length) * 2 * Math.PI - Math.PI / 2;
    const currentR = (sub.currentScore / 10) * radius;
    const potentialR = (sub.potentialScore / 10) * radius;
    const labelX = center + Math.cos(angle) * labelRadius;
    const labelY = center + Math.sin(angle) * labelRadius;

    return {
      ...sub,
      angle,
      current: {
        x: center + Math.cos(angle) * currentR,
        y: center + Math.sin(angle) * currentR,
      },
      potential: {
        x: center + Math.cos(angle) * potentialR,
        y: center + Math.sin(angle) * potentialR,
      },
      label: { x: labelX, y: labelY },
    };
  });

  const currentPath =
    points
      .map(
        (point, i) =>
          `${i === 0 ? "M" : "L"} ${point.current.x} ${point.current.y}`
      )
      .join(" ") + " Z";

  const potentialPath =
    points
      .map(
        (point, i) =>
          `${i === 0 ? "M" : "L"} ${point.potential.x} ${point.potential.y}`
      )
      .join(" ") + " Z";

  return (
    <div className="space-y-6">
      <div className="flex justify-center">
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          className="w-full h-auto max-w-md"
        >
          {/* Grid circles */}
          {[2, 4, 6, 8, 10].map((value) => (
            <circle
              key={value}
              cx={center}
              cy={center}
              r={(value / 10) * radius}
              fill="none"
              stroke="hsl(var(--border))"
              strokeWidth="1"
              opacity="0.3"
            />
          ))}

          {/* Grid lines */}
          {points.map((point, index) => (
            <line
              key={index}
              x1={center}
              y1={center}
              x2={center + Math.cos(point.angle) * radius}
              y2={center + Math.sin(point.angle) * radius}
              stroke="hsl(var(--border))"
              strokeWidth="1"
              opacity="0.3"
            />
          ))}

          {/* Potential area (dashed) */}
          <path
            d={potentialPath}
            fill="#DCFF02"
            fillOpacity="0.1"
            stroke="#DCFF02"
            strokeWidth="2"
            strokeDasharray="6,4"
            className={`transition-all duration-1000 ${
              animated ? "opacity-100" : "opacity-0"
            }`}
          />

          {/* Current area (solid) */}
          <path
            d={currentPath}
            fill="#DCFF02"
            fillOpacity="0.2"
            stroke="#DCFF02"
            strokeWidth="3"
            className={`transition-all duration-1000 delay-300 ${
              animated ? "opacity-100" : "opacity-0"
            }`}
          />

          {/* Labels */}
          {points.map((point, index) => {
            const IconComponent = point.icon;

            return (
              <g key={`${point.category}-${point.name}`}>
                {/* Icon background */}
                <circle
                  cx={point.label.x}
                  cy={point.label.y - 12}
                  r="12"
                  fill={point.categoryColor}
                  className={`transition-all duration-500 delay-${
                    (index + 1) * 100
                  } ${animated ? "opacity-100" : "opacity-0"}`}
                />

                {/* Icon */}
                <foreignObject
                  x={point.label.x - 6}
                  y={point.label.y - 18}
                  width="12"
                  height="12"
                  className={`transition-all duration-500 delay-${
                    (index + 1) * 100
                  } ${animated ? "opacity-100" : "opacity-0"}`}
                >
                  <IconComponent className="w-3 h-3 text-white" />
                </foreignObject>

                {/* Label text */}
                <text
                  x={point.label.x}
                  y={point.label.y + 6}
                  textAnchor="middle"
                  className={`text-xs font-bold fill-current text-foreground transition-all duration-500 delay-${
                    (index + 1) * 100
                  } ${animated ? "opacity-100" : "opacity-0"}`}
                >
                  {point.name}
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      {/* Description below chart */}
      <div className="text-center space-y-4 max-w-md mx-auto">
        <div className="flex items-center justify-center gap-8">
          <div className="flex items-center gap-3 bg-card border border-border px-4 py-3 rounded-lg">
            <div
              className="w-6 h-1 rounded-full"
              style={{ backgroundColor: "#DCFF02" }}
            ></div>
            <span className="text-sm font-medium text-foreground">
              Your scores now
            </span>
          </div>
          <div className="flex items-center gap-3 bg-card border border-border px-4 py-3 rounded-lg">
            <div
              className="w-6 h-1 border-t-2 border-dashed rounded-full"
              style={{ borderColor: "#DCFF02" }}
            ></div>
            <span className="text-sm font-medium text-foreground">
              After 1 year with 1440
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
